import { useCallback, use, useEffect, useMemo, useRef, useState } from 'react';

import {
  filter,
  isEqual,
  isPlainObject,
  isString,
  merge,
  omit
} from 'lodash-es';
import {
  FieldErrors,
  FieldValues,
  useForm,
  type UseFormProps,
  type DefaultValues,
  type Resolver,
  type Path
} from 'react-hook-form';
import { ModelCreateContext } from '../components/models/ModelCreateProvider';
import { ModelEditContext } from '../components/models/ModelEditProvider';
import { ModelIndexContext } from '../components/models/ModelIndexProvider';
import { ModelShowContext } from '../components/models/ModelShowProvider';
import { useModel } from './models';
import {
  modelKey,
  useModelCreate,
  useModelDelete,
  useModelIndex,
  useModelShow,
  useModelUpdate
} from './queries';
import type { RhinoIndexPage } from './queries';
import {
  getBaseOwnerFilters,
  getParentModel,
  getReferenceAttributes,
  isIdentifier
} from '../utils/models';
import { useDebouncedCallback } from '@tanstack/react-pacer';
import {
  getCreatableAttributes,
  getUpdatableAttributes
} from '../utils/models';
import { useDefaultValues, useResolver, useSchema } from './form';
import { usePaths, type PathsInput } from './paths';
import { z } from 'zod';
import { useBaseOwnerId } from './owner';
import { ModelFiltersContext } from '../components/models/ModelFiltersProvider';
import { zodFiltersFromAttribute } from '../utils/zod';
import {
  Resources,
  RhinoAttributePaths,
  RhinoFilter,
  RhinoOrder,
  RhinoResource,
  RhinoResourceName,
  RhinoResourceSpecifier,
  RhinoResourceSpecifierToResource,
  RhinoResourceSpecifierToName,
  RhinoCreate
} from '..';
import {
  useQuery,
  keepPreviousData,
  UseMutationOptions,
  UseQueryOptions
} from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { networkApiCallOnlyData, NetworkParamError } from '../lib/networking';

import {
  useFormAnalytics,
  useTrackedForm,
  useIndexAnalytics
} from './controllerAnalytics';

export const DEFAULT_LIMIT = 10;

const useFormBuildErrors = (): [
  FieldErrors | undefined,
  UseMutationOptions<unknown, Error, unknown>['onError'],
  () => void
] => {
  const [errors, setErrors] = useState<FieldErrors | undefined>(undefined);

  const onError = useCallback((e: Error) => {
    if (!(e instanceof NetworkParamError)) {
      return;
    }

    // Errors can be an array of strings or an object with keys
    // If it is an array of strings, we assume it is a base error
    if (Array.isArray(e.errors)) {
      setErrors({
        // @ts-expect-error FIXME: need better typing
        root: {
          message: e.errors?.[0]
        }
      });
      return;
    }

    // The const holds the narrowing from the guard above; reading e.errors
    // inside the callback widens it back to the union.
    const fieldErrors = e.errors;

    const newErrors = Object.keys(fieldErrors).reduce((errorObj, name) => {
      const key = name === 'base' ? 'root' : name;

      errorObj[key] = {
        type: 'manual',
        message: fieldErrors[name][0]
      };

      return errorObj;
    }, {} as FieldErrors);

    setErrors(newErrors);
  }, []);

  const clearErrors = useCallback(() => setErrors(undefined), []);
  return [errors, onError, clearErrors];
};

// FIXME: This should be calculated as the first sortable field found in the model
export const DEFAULT_SORT = ['-updated_at'];

// Omitting an order takes the default; null asks for none at all.
export const defaultOrderTerms = (order: unknown) =>
  order === undefined ? DEFAULT_SORT : ((order as string[] | null) ?? []);

// FIXME the calculation of first page number to show should be handled in the pager not in the controller
const MAX_PAGES = 3;

export const useModelIndexContext = () => {
  const context = use(ModelIndexContext);

  if (context === undefined) {
    throw new Error(
      'useModelIndexContext must be used within a ModelIndexProvider'
    );
  }
  return context;
};

// https://chat.openai.com/share/55cc13f5-99ae-43f4-9781-15c0016861e9
// Finds the keys unique to obj2
const findDeepDifference = (
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>
) => {
  const differences = {} as Record<string, unknown>;

  const compare = (
    item1: Record<string, unknown>,
    item2: Record<string, unknown>,
    path = []
  ) => {
    if (isPlainObject(item2)) {
      Object.keys(item2).forEach((key) => {
        if (
          !item1 ||
          !Object.prototype.hasOwnProperty.call(item1, key) ||
          isPlainObject(item2[key])
        ) {
          // @ts-expect-error FIXME: need better typing
          compare(item1 && item1[key], item2[key], path.concat(key));
        }
      });
    } else if (
      !item1 ||
      !Object.prototype.hasOwnProperty.call(item1, path[path.length - 1])
    ) {
      // Assign value only if the key does not exist in obj1
      let current = differences;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]]) current[path[i]] = {};
        current = current[path[i]] as Record<string, unknown>;
      }
      current[path[path.length - 1]] = item2;
    }
  };

  compare(obj1, obj2);

  return differences;
};

// https://chat.openai.com/share/55cc13f5-99ae-43f4-9781-15c0016861e9
// Counts the number of leaf nodes in an object
const countLeafNodes = (obj: Record<string, unknown>) => {
  const reducer = (acc: number, value: unknown): number => {
    if (isPlainObject(value)) {
      return (
        acc + Object.values(value as Record<string, unknown>).reduce(reducer, 0)
      );
    } else {
      return acc + 1;
    }
  };

  return Object.values(obj).reduce(reducer, 0);
};

export type UseModelIndexControllerOptions<T extends RhinoResourceSpecifier> = {
  // Static UI identifier, never a URL, record ID or user-provided value.
  analyticsSurface?: string;
  defaultFiltersBaseOwner?: boolean;
  defaultFilter?: RhinoFilter<T>;
  defaultLimit?: number;
  defaultOffset?: number;
  defaultOrder?: RhinoOrder<T> | null;
  defaultSearch?: string;
  defaultGeospatial?: object;
  initialState?: {
    search?: string;
    order?: RhinoOrder<T> | null;
    limit?: number;
    offset?: number;
    filter?: RhinoFilter<T>;
    geospatial?: Record<string, unknown>;
  };
  // Merged into the matching mutation. This controller owns three, so they are
  // named individually rather than sharing one `mutationOptions`. An onSuccess
  // here takes the place of the default invalidation for that mutation.
  createOptions?: Parameters<typeof useModelCreate<T>>[1];
  destroyOptions?: Parameters<typeof useModelDelete<T>>[1];
  model: T;
  paths?: RhinoAttributePaths<T>;
  queryOptions?: Partial<UseQueryOptions<RhinoIndexPage<T>>>;
  networkOptions?: Partial<AxiosRequestConfig>;
  updateOptions?: Parameters<typeof useModelUpdate<T>>[1];
};

// Reset/change default state - when baseOwnerFilter changes for instance or tabbed filtering
//      Does initial state need to be reset?
// Count of filters
export const useModelIndexController = <T extends RhinoResourceSpecifier>(
  options: UseModelIndexControllerOptions<T>
) => {
  const model = useModel(options.model);
  const {
    createOptions,
    defaultFiltersBaseOwner = true,
    destroyOptions,
    updateOptions
  } = options;
  const baseOwnerId = useBaseOwnerId();

  const defaultStateRef = useRef({
    filter: (defaultFiltersBaseOwner
      ? (merge(
          getBaseOwnerFilters(model, baseOwnerId),
          options?.defaultFilter
        ) ?? {})
      : (options?.defaultFilter ?? {})) as Record<string, unknown>,
    limit: options?.defaultLimit ?? DEFAULT_LIMIT,
    offset: options?.defaultOffset ?? 0,
    order: defaultOrderTerms(options?.defaultOrder),
    search: options?.defaultSearch ?? '',
    geospatial: (options?.defaultGeospatial ?? {}) as Record<string, unknown>
  });

  const storedInitialStateRef = useRef<{
    search?: string;
    order?: string[];
    limit?: number;
    offset?: number;
    filter?: Record<string, unknown>;
    geospatial?: Record<string, unknown>;
  } | null>(null);

  // https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  if (storedInitialStateRef.current === null) {
    // When computing the initial state of filters, the URL has precedence over the baseFilters for everything except filters.
    // That means that if baseFilter has { order: 'a' } and the URL has ?order=b, the initial state of searchParams will have
    // { order: 'b' }, as it is the order value in the URL.

    // The filters key in the baseFilters object, however, represent implicit, fixed filters, meaning they have precedence
    // over anything else. They cannot be changed by setting URL, nor by the UI, nor they render pills of their own.
    // The initial value of searchParams will be a merge of filters from the URL and the baseFiltes, the latter being able to
    // override anything in the URL.

    const initialState = options.initialState ?? {};

    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current = {
      // eslint-disable-next-line react-hooks/refs
      search: initialState.search ?? defaultStateRef.current?.search,
      order:
        // eslint-disable-next-line react-hooks/refs
        initialState.order === undefined
          ? // eslint-disable-next-line react-hooks/refs
            defaultStateRef.current?.order
          : defaultOrderTerms(initialState.order),
      // eslint-disable-next-line react-hooks/refs
      limit: initialState.limit || defaultStateRef.current?.limit,
      // eslint-disable-next-line react-hooks/refs
      offset: initialState.offset || defaultStateRef.current?.offset,
      // Merge the filters from the URL with the filters from the baseFilters, the latter having precedence
      // This handles cases such as project.client.id in the filters and project.id in the baseFilters
      // If we did not merge, the project.client.id would be lost
      filter: merge(
        {},
        initialState.filter ?? {},
        // eslint-disable-next-line react-hooks/refs
        defaultStateRef.current?.filter
      ),
      geospatial: merge(
        {},
        initialState.geospatial ?? {},
        // eslint-disable-next-line react-hooks/refs
        defaultStateRef.current?.geospatial
      )
    };
  }

  // eslint-disable-next-line react-hooks/refs
  const [internalFilter, setInternalFilter] = useState(() =>
    findDeepDifference(
      defaultStateRef.current.filter,
      storedInitialStateRef.current!.filter!
    )
  );
  const [fullFilter, setFullFilter] = useState<Record<string, unknown>>(
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.filter!
  );
  // eslint-disable-next-line react-hooks/refs
  const [internalGeospatial, setInternalGeospatial] = useState(() =>
    findDeepDifference(
      defaultStateRef.current.geospatial,
      storedInitialStateRef.current!.geospatial!
    )
  );
  const [fullGeospatial, setFullGeospatial] = useState(
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.geospatial
  );
  const [limit, setLimit] = useState<number>(
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.limit!
  );
  const [offset, setOffset] = useState<number>(
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.offset!
  );
  const [order, setOrder] = useState<string[]>(
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.order!
  );
  const [search, setSearch] = useState<string>(
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.search!
  );

  const setFilter = useCallback((filter: Record<string, unknown>) => {
    setInternalFilter(
      findDeepDifference(defaultStateRef.current.filter, filter)
    );
    setFullFilter(merge({}, filter ?? {}, defaultStateRef.current?.filter));
  }, []);

  const setDefaultFilter = useCallback(
    (defaultFilter: Record<string, unknown>) => {
      const updatedDefaultFilter = defaultFiltersBaseOwner
        ? merge({}, getBaseOwnerFilters(model, baseOwnerId), defaultFilter)
        : defaultFilter;

      defaultStateRef.current.filter = updatedDefaultFilter ?? {};

      const newFilter = findDeepDifference(
        defaultStateRef.current.filter,
        internalFilter
      );
      const newFullFilter = merge(
        {},
        newFilter,
        defaultStateRef.current?.filter
      );

      if (storedInitialStateRef.current) {
        storedInitialStateRef.current.filter = newFullFilter;
      }

      setInternalFilter(newFilter);
      setFullFilter(newFullFilter);
    },
    [baseOwnerId, defaultFiltersBaseOwner, internalFilter, model]
  );

  // A reused screen can change organization or team without remounting.
  // Reapply fixed filters and reset pagination when that scope changes.
  const scopeOwnerId = defaultFiltersBaseOwner ? baseOwnerId : undefined;
  const previousScopeRef = useRef({
    baseOwnerId: scopeOwnerId,
    filter: options.defaultFilter
  });
  useEffect(() => {
    const scope = { baseOwnerId: scopeOwnerId, filter: options.defaultFilter };
    if (isEqual(previousScopeRef.current, scope)) return;
    previousScopeRef.current = scope;
    setDefaultFilter(options.defaultFilter ?? {});
    setOffset(defaultStateRef.current.offset);
  }, [scopeOwnerId, options.defaultFilter, setDefaultFilter]);

  const setGeospatial = useCallback((geospatial: Record<string, unknown>) => {
    setInternalGeospatial(
      findDeepDifference(defaultStateRef.current.geospatial, geospatial)
    );
    setFullGeospatial(
      merge({}, geospatial ?? {}, defaultStateRef.current?.geospatial)
    );
  }, []);

  const setDefaultGeospatial = useCallback(
    (defaultGeospatial: Record<string, unknown>) => {
      defaultStateRef.current.geospatial = defaultGeospatial ?? {};

      const newGeospatial = findDeepDifference(
        defaultStateRef.current.geospatial,
        internalGeospatial
      );
      const newFullGeospatial = merge(
        {},
        newGeospatial,
        defaultStateRef.current?.geospatial
      );

      if (storedInitialStateRef.current) {
        storedInitialStateRef.current.geospatial = newFullGeospatial;
      }

      setInternalGeospatial(newGeospatial);
      setFullGeospatial(newFullGeospatial);
    },
    [internalGeospatial]
  );

  const query = useModelIndex(options.model, {
    filter: fullFilter,
    geospatial: fullGeospatial,
    limit,
    offset,
    order: order as RhinoOrder<T>,
    search,
    queryOptions: {
      placeholderData: keepPreviousData,
      ...options.queryOptions
    },
    networkOptions: options?.networkOptions
  });

  const totalFilters = useMemo(
    () => countLeafNodes(internalFilter),
    [internalFilter]
  );
  useIndexAnalytics(
    model.model,
    {
      search,
      filter: internalFilter,
      fullFilter,
      geospatial: internalGeospatial,
      order,
      limit,
      offset
    },
    totalFilters,
    query,
    // This initial offset is fixed for the controller lifetime, like its initial state above.
    // eslint-disable-next-line react-hooks/refs
    storedInitialStateRef.current.offset!,
    options.analyticsSurface
  );

  const totalFullFilters = useMemo(
    () => countLeafNodes(fullFilter),
    [fullFilter]
  );

  const create = useModelCreate(
    options.model,
    { ...createOptions },
    undefined,
    options.networkOptions
  );
  const update = useModelUpdate(
    options.model,
    { ...updateOptions },
    undefined,
    options.networkOptions
  );
  const destroy = useModelDelete(
    options.model,
    { ...destroyOptions },
    options.networkOptions
  );

  // Pagination
  const totalPages = useMemo(
    () => Math.ceil((query.total ?? 0) / limit),
    [query.total, limit]
  );
  const page = useMemo(() => Math.round(offset / limit + 0.5), [offset, limit]);
  const firstPage = useMemo(
    () =>
      Math.max(
        1,
        Math.min(
          page - Math.round(MAX_PAGES / 2 - 0.5),
          totalPages - MAX_PAGES + 1
        )
      ),
    [page, totalPages]
  );
  const lastPage = useMemo(
    () => Math.min(firstPage + Math.round(MAX_PAGES / 2 + 0.5), totalPages),
    [firstPage, totalPages]
  );
  const hasPrevPage = offset > 0;
  const hasNextPage = offset + limit < totalPages * limit;
  const setPage = (page: number) => setOffset((page - 1) * limit);

  const isEqualToDefault = useMemo(
    () =>
      isEqual(
        {
          filter: fullFilter,
          geospatial: fullGeospatial,
          limit,
          offset,
          order,
          search
        },
        // eslint-disable-next-line react-hooks/refs
        defaultStateRef.current
      ),
    [fullFilter, fullGeospatial, limit, offset, order, search]
  );

  useEffect(
    // This will definitely be set by now
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    () => setOffset(storedInitialStateRef.current!.offset!),
    [internalFilter, internalGeospatial, search, limit]
  );

  // eslint-disable-next-line react-hooks/refs
  return {
    // eslint-disable-next-line react-hooks/refs
    defaultState: defaultStateRef.current,
    // eslint-disable-next-line react-hooks/refs
    initialState: storedInitialStateRef.current,
    isEqualToDefault,
    order,
    setOrder,
    search,
    setSearch,
    filter: internalFilter,
    setFilter,
    totalFilters,
    fullFilter,
    totalFullFilters,
    setDefaultFilter,
    geospatial: internalGeospatial,
    setGeospatial,
    fullGeospatial,
    setDefaultGeospatial,
    limit,
    setLimit,
    offset,
    setOffset,
    totalPages,
    page,
    hasPrevPage,
    hasNextPage,
    firstPage,
    lastPage,
    setPage,
    ...query,
    create,
    update,
    delete: destroy,
    destroy
  };
};

export const useModelShowContext = () => {
  const context = use(ModelShowContext);

  if (context === undefined) {
    throw new Error(
      'useModelShowContext must be used within a ModelShowProvider'
    );
  }
  return context;
};

const getViewablePaths = (model: RhinoResource) =>
  filter(model.properties, (a) => {
    return (
      !isIdentifier(a) &&
      a.name !== model.ownedBy &&
      // display_name duplicates the attribute it derives from (name/title),
      // so default tables leave it out; explicit paths can still use it
      a.name !== 'display_name' &&
      !(a.type === 'array' && a.readOnly) &&
      a.writeOnly !== true
    );
  }).map((a) => a.name);

export type UseModelShowControllerOptions<T extends RhinoResourceSpecifier> = {
  // Merged into the matching mutation. This controller owns three, so they are
  // named individually rather than sharing one `mutationOptions`. An onSuccess
  // here takes the place of the default invalidation for that mutation.
  createOptions?: Parameters<typeof useModelCreate<T>>[1];
  destroyOptions?: Parameters<typeof useModelDelete<T>>[1];
  extraDefaultValues?: object;
  formOptions?: UseFormProps;
  model: T;
  modelId: string | number;
  paths?: PathsInput<RhinoAttributePaths<T>>;
  queryOptions?: Partial<UseQueryOptions<RhinoResourceSpecifierToResource<T>>>;
  networkOptions?: Partial<AxiosRequestConfig>;
  updateOptions?: Parameters<typeof useModelUpdate<T>>[1];
};

export const useModelShowController = <T extends RhinoResourceSpecifier>(
  options: UseModelShowControllerOptions<T>
) => {
  const model = useModel(options.model);
  const {
    createOptions,
    destroyOptions,
    extraDefaultValues,
    modelId,
    paths,
    updateOptions
  } = options;
  const [errors, onError] = useFormBuildErrors();

  const query = useModelShow(options.model, modelId, {
    queryOptions: options?.queryOptions,
    networkOptions: options?.networkOptions
  });
  const { resource } = query;

  const create = useModelCreate(
    options.model,
    { onError, ...createOptions },
    undefined,
    options.networkOptions
  );
  const update = useModelUpdate(
    options.model,
    { onError, ...updateOptions },
    undefined,
    options.networkOptions
  );
  const destroy = useModelDelete(
    options.model,
    { onError, ...destroyOptions },
    options.networkOptions
  );

  const pathsOrDefault = useMemo(
    () => paths || getViewablePaths(model),
    [paths, model]
  );
  const computedPaths = usePaths(pathsOrDefault, resource);

  const schema = useSchema(model, computedPaths);
  const defaultValues = useDefaultValues(model, computedPaths, {
    extraDefaultValues
  });
  const resolver = useResolver(schema);

  const methods = useForm({
    defaultValues,
    errors,
    resolver,
    // resource may be null while loading; the prior yup-typed code passed it
    // through unchanged and RHF tolerates a null `values`. Cast through unknown
    // to satisfy FieldValues without altering the runtime value.
    values: resource as unknown as FieldValues | undefined,
    resetOptions: {
      // user-interacted input will be retained
      keepDirtyValues: true
    },
    ...options.formOptions
  });

  return {
    modelId,
    methods,
    paths: computedPaths,
    resolver,
    schema,
    ...query,
    create,
    update,
    delete: destroy,
    destroy
  };
};

export const useModelCreateContext = () => {
  const context = use(ModelCreateContext);

  if (context === undefined) {
    throw new Error(
      'useModelCreateContext must be used within a ModelCreateProvider'
    );
  }
  return context;
};

// We removed ownedBy from the creatable attributes because it is set automatically
// We removed anyOf with more than one element because we don't support them automatically
const getCreatablePaths = (model: RhinoResource) =>
  getCreatableAttributes(model)
    .filter((a) => a.name !== model.ownedBy)
    .filter((a) => !a.anyOf || a.anyOf?.length <= 1)
    .map((a) => a.name);

type CreateValues<T extends RhinoResourceSpecifier> = Extract<
  RhinoCreate<RhinoResourceSpecifierToName<T>>,
  FieldValues
>;

export type UseModelCreateControllerOptions<T extends RhinoResourceSpecifier> =
  {
    autoFocus?: boolean | RhinoAttributePaths<T>[number];
    disabled?: boolean;
    extraDefaultValues?: object;
    model: T;
    // Merged into the create mutation, so a caller can replace what a save
    // invalidates. An onSuccess here takes the place of the default.
    mutationOptions?: Parameters<typeof useModelCreate<T>>[1];
    parentId?: string | number;
    paths?: PathsInput<RhinoAttributePaths<T>>;
    queryOptions?: Partial<UseQueryOptions<unknown>>;
    networkOptions?: AxiosRequestConfig;
    formOptions?: UseFormProps<CreateValues<T>>;
    transformSchema?: (schema: z.ZodObject<z.ZodRawShape>) => z.ZodType;
  };

export const useModelCreateController = <T extends RhinoResourceSpecifier>(
  options: UseModelCreateControllerOptions<T>
) => {
  const model = useModel(options.model);
  const {
    extraDefaultValues,
    autoFocus = true,
    mutationOptions,
    parentId,
    paths,
    queryOptions,
    transformSchema
  } = options;
  const [errors, onError, clearErrors] = useFormBuildErrors();

  const analytics = useFormAnalytics(model.model, 'create', parentId);

  const mutation = useModelCreate(
    options.model,
    {
      onMutate: clearErrors,
      onError,
      ...mutationOptions
    },
    analytics,
    options.networkOptions
  );

  // Fetch the parent model for the owner value and the breadcrumb
  const parentModel = useMemo(() => getParentModel(model), [model]);

  const parentQuery = useQuery({
    queryKey: parentModel
      ? [
          modelKey(parentModel, 'show'),
          ...(parentModel.singular ? [] : [String(parentId)])
        ]
      : ['rhino-no-parent'],
    queryFn: ({ signal }) => {
      if (!parentModel) throw new Error('This model has no parent');
      const path = parentModel.singular
        ? parentModel.path
        : `${parentModel.path}/${parentId}`;
      return networkApiCallOnlyData(path, {
        ...options.networkOptions,
        signal
      });
    },
    ...queryOptions,
    // A global model must never fetch an undefined parent, even if requested.
    enabled:
      !!parentModel && parentId != null && (queryOptions?.enabled ?? true)
  });
  const showParent = {
    ...parentQuery,
    model: parentModel,
    resource: parentQuery.data ?? null
  };

  const pathsOrDefault = useMemo(
    () => paths || getCreatablePaths(model),
    [paths, model]
  );
  // FIXME: Do I need to pass the fake resource with the parent id?
  const computedPaths = usePaths(pathsOrDefault, {});

  const modelSchema = useSchema(model, computedPaths);
  const schema = useMemo(
    () => (transformSchema ? transformSchema(modelSchema) : modelSchema),
    [modelSchema, transformSchema]
  );
  const defaultValues = useDefaultValues(model, computedPaths, {
    // A globally owned model has no owner to seed, and keying off a null one
    // would default a literal "null" attribute.
    extraDefaultValues: {
      ...(model.ownedBy && model.ownedBy !== 'global'
        ? { [model.ownedBy]: parentId }
        : {}),
      ...extraDefaultValues
    }
  });
  const resolver = useResolver(schema);

  type Values = CreateValues<T>;
  const methods = useForm<Values>({
    defaultValues: defaultValues as DefaultValues<Values>,
    disabled: options?.disabled ?? (showParent.isLoading || mutation.isPending),
    errors: errors as FieldErrors<Values> | undefined,
    resolver: resolver as unknown as Resolver<Values>,
    ...options.formOptions
  });
  const trackedMethods = useTrackedForm(
    methods,
    analytics,
    !showParent.isLoading
  );
  const { setFocus } = methods;

  useEffect(() => {
    // If the focus is true, set the focus to the first path if it is a string
    if (autoFocus === true && isString(computedPaths?.[0])) {
      setFocus(computedPaths?.[0] as Path<Values>);
      // If the focus is a string (not false), set the focus to that path
    } else if (isString(autoFocus)) {
      setFocus(autoFocus as Path<Values>);
    }
  }, [computedPaths, autoFocus, setFocus]);

  const { reset: resetMutation } = mutation;
  const { clearErrors: clearFormErrors } = methods;
  const clearServerErrors = useCallback(() => {
    clearErrors();
    clearFormErrors();
    resetMutation();
  }, [clearErrors, clearFormErrors, resetMutation]);

  return {
    clearServerErrors,
    parentId,
    parentModel,
    ...mutation,
    showParent,
    methods: trackedMethods,
    paths: computedPaths,
    resolver,
    schema
  };
};

export const useModelEditContext = () => {
  const context = use(ModelEditContext);

  if (context === undefined) {
    throw new Error(
      'useModelEditContext must be used within a ModelEditProvider'
    );
  }
  return context;
};

// We removed ownedBy from the updatable attributes because it is set automatically
const getEditablePaths = (model: RhinoResource) =>
  getUpdatableAttributes(model)
    .filter((a) => a.name !== model.ownedBy)
    .map((a) => a.name);

export type UseModelEditControllerOptions<T extends RhinoResourceSpecifier> = {
  autoFocus?:
    | boolean
    | (T extends RhinoResourceName
        ? keyof Resources[T]
        : T extends RhinoResource
          ? keyof Resources[T['model']]
          : never);
  debounceDelay?: number;
  disabled?: boolean;
  extraDefaultValues?: object;
  model: T;
  modelId?: string | number;
  // Merged into the update mutation, so a caller can replace what a save
  // invalidates. An onSuccess here takes the place of the default, which
  // invalidates both the record and the index.
  mutationOptions?: Parameters<typeof useModelUpdate<T>>[1];
  paths?: RhinoAttributePaths<T>;
  queryOptions?: UseQueryOptions;
  networkOptions?: AxiosRequestConfig;
};

export const useModelEditController = <T extends RhinoResourceSpecifier>(
  options: UseModelEditControllerOptions<T>
) => {
  const model = useModel(options.model);
  const {
    modelId,
    extraDefaultValues,
    autoFocus = true,
    paths,
    debounceDelay = 2000,
    mutationOptions,
    queryOptions
  } = options;
  const [errors, onError] = useFormBuildErrors();

  const analytics = useFormAnalytics(model.model, 'edit', modelId);

  const mutation = useModelUpdate(
    options.model,
    {
      onError,
      ...mutationOptions
    },
    analytics,
    options.networkOptions
  );
  const debouncedMutate = useDebouncedCallback(
    (...args: Parameters<typeof mutation.mutate>) => mutation.mutate(...args),
    {
      wait: debounceDelay,
      key: `ModelEdit:${model.model}`,
      // An edit still inside the debounce window when the form goes away is an
      // edit the user made and expects to keep, so send it rather than take the
      // default and drop it. Unmounting is the normal way out of an autosaving
      // form — navigating away, switching records, closing a modal.
      onUnmount: (debouncer) => debouncer.flush()
    }
  );

  // A modal for instance may not have a modelId yet
  const show = useModelShow(model, modelId, {
    // @ts-expect-error FIXME: why enabled error?
    queryOptions: { enabled: !!modelId, ...queryOptions },
    networkOptions: options.networkOptions
  });

  const { resource } = show;

  const pathsOrDefault = useMemo(
    () => paths || getEditablePaths(model),
    [paths, model]
  );
  const computedPaths = usePaths(pathsOrDefault, resource);

  const schema = useSchema(model, computedPaths);
  const defaultValues = useDefaultValues(model, computedPaths, {
    extraDefaultValues
  });
  const resolver = useResolver(schema);

  const methods = useForm({
    defaultValues,
    errors,
    resolver,
    // resource may be null while loading; the prior yup-typed code passed it
    // through unchanged and RHF tolerates a null `values`. Cast through unknown
    // to satisfy FieldValues without altering the runtime value.
    values: resource as unknown as FieldValues | undefined,
    resetOptions: {
      // user-interacted input will be retained
      keepDirtyValues: true
    },
    ...options,
    // Must follow the spread, which carries the caller's own `disabled`. A form
    // whose values have not arrived yet must not be editable: the fetch lands as
    // a reset, and an edit made in that window is either overwritten or appended
    // to, depending on whether the field was dirtied. Loading is therefore a
    // hard gate, and the caller's value only replaces the pending default, so an
    // autosaving form can stay live between saves without reopening the window.
    disabled: show.isLoading || (options?.disabled ?? mutation.isPending)
  });
  const trackedMethods = useTrackedForm(
    methods,
    analytics,
    !!resource && !show.isLoading
  );
  const { setFocus } = methods;

  useEffect(() => {
    // If the focus is true, set the focus to the first path if it is a string
    if (autoFocus === true && isString(computedPaths?.[0])) {
      setFocus(computedPaths?.[0]);

      // If the focus is a string (not false), set the focus to that path
    } else if (isString(autoFocus)) {
      setFocus(autoFocus);
    }
  }, [computedPaths, autoFocus, setFocus]);

  return {
    modelId,
    ...mutation,
    debouncedMutate,
    show,
    methods: trackedMethods,
    paths: computedPaths,
    resolver,
    schema
  };
};

export const useModelFiltersContext = () => {
  const context = use(ModelFiltersContext);

  if (context === undefined) {
    throw new Error(
      'useModelFiltersContext must be used within a ModelFiltersProvider'
    );
  }
  return context;
};

export const createFilteredObject = (obj: Record<string, unknown>) => {
  const result = {} as Record<string, unknown>;

  // iterate through all keys in the object
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // if the value is not undefined, add it to the new object
      if (obj[key] !== undefined) {
        // Check if the value is an array for "in" style filters
        if (Array.isArray(obj[key])) {
          // Directly copy the array
          result[key] = obj[key];
        }
        // if the value is an object (but not an array), recursively call the function
        else if (typeof obj[key] === 'object') {
          result[key] = createFilteredObject(
            obj[key] as Record<string, unknown>
          );
          // if the object is now empty, don't add it to the new object
          if (
            Object.keys(result[key] as Record<string, unknown>).length === 0
          ) {
            delete result[key];
          }
        } else {
          // If it's not an array or object, copy the value
          result[key] = obj[key];
        }
      }
    }
  }
  return result;
};

export type UseModelFiltersControllerOptions = {
  extraDefaultValues?: object;
  paths?: string[];
};

export const useModelFiltersController = (
  options: UseModelFiltersControllerOptions
) => {
  const { setFilter, initialState, model } = useModelIndexContext();
  const { extraDefaultValues, paths } = options;
  const [pills, setPills] = useState({});

  const pathsOrDefault = useMemo(
    () =>
      paths ||
      getReferenceAttributes(model)
        .filter(
          (a) => a.name !== model.ownedBy && !a.name.endsWith('_attachment')
        )
        .filter((a) => !a.anyOf || a.anyOf?.length <= 1)
        .map((a) => a.name),
    [paths, model]
  );
  const computedPaths = usePaths(pathsOrDefault);

  const schema = useSchema(model, computedPaths, {
    schemaFromAttribute: zodFiltersFromAttribute
  });
  const defaultValues = useDefaultValues(model, computedPaths, {
    extraDefaultValues,
    schemaFromAttribute: zodFiltersFromAttribute
  });
  const resolver = useResolver(schema);

  const methods = useForm({
    defaultValues,
    values: initialState.filter,
    resolver,

    // Keep the default values because the initial state is likely to be sparse
    resetOptions: { keepDefaultValues: true }
  });
  const { subscribe } = methods;

  useEffect(() => {
    return subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        // Only pass the defined and non-null values to the filter
        setFilter(createFilteredObject(values));
      }
    });
  }, [setFilter, subscribe]);

  const setPill = useCallback(
    (path: string, value: string) => {
      setPills((pills) => ({ ...pills, [path]: value }));
    },
    [setPills]
  );

  const resetPill = useCallback(
    (path: string) => setPills((pills) => omit(pills, path)),
    [setPills]
  );

  return {
    model,
    defaultValues,
    methods,
    paths: computedPaths,
    pills,
    setPill,
    resetPill,
    setPills
  };
};
